import * as React from "react"
import { InternalLink, ExternalLink, ReferenceLink } from "blog/components/link"
import {
  LeafNode,
  NavigationNode,
  NodeKind,
  NodeValue,
  PageNode,
  PostNode,
} from "blog/json_nodes"
import {
  Text,
  List,
  Navigation,
  Key,
  Delimiter,
  Page,
  Post,
  HtmlText,
  Value,
  Entry,
} from "blog/components/json_lego"

export const JSONRenderer = ({ json }: { json: LeafNode }) => {
  return <Entry>{renderNode(json, "")}</Entry>
}

function renderNode(value: NodeValue, _: string): React.ReactNode {
  if (typeof value === "string") {
    return <Text>{value}</Text>
  }

  if (Array.isArray(value)) {
    const renderedValues = value.map((v, _) => renderNode(v, ""))
    return <List values={renderedValues} />
  }

  if (typeof value === "object") {
    switch (value.kind) {
      case NodeKind.NAVIGATION:
        return <RenderNavigation value={value} />
      case NodeKind.EXTERNAL_LINK:
        return <ExternalLink link={value} />
      case NodeKind.REFERENCE_LINK:
        return <ReferenceLink link={value} />
      case NodeKind.INTERNAL_LINK:
        return <InternalLink link={value} />
      case NodeKind.PAGE:
        return <RenderPage value={value} />
      case NodeKind.POST:
        return <RenderPost value={value} />
      default:
        throw new Error("Incorrect kind " + JSON.stringify(value))
    }
  }

  throw new Error("Should never hit this")
}

const RenderPage = ({ value }: { value: PageNode }) => (
  <Page>
    <Value>{renderNode(value.navigation, "")}</Value>
    <Value>{renderNode(value.content, "")}</Value>
  </Page>
)

const RenderPost = ({ value }: { value: PostNode }) => (
  <Post>
    <Entry trailing={true}>
      <Key label="title" />
      <Delimiter />
      <Value>{renderNode(value.title, "")}</Value>
    </Entry>
    <Entry trailing={true}>
      <Key label="tags" />
      <Delimiter />
      <Value>{renderNode(value.tags, "")}</Value>
    </Entry>
    <Entry trailing={true}>
      <Key label="date" />
      <Delimiter />
      <Value>{renderNode(value.date, "")}</Value>
    </Entry>
    <Entry trailing={true}>
      <Key label="content" />
      <Delimiter />
      <Value>
        <HtmlText value={value.content} />
      </Value>
    </Entry>
    <Entry trailing={true}>
      <Key label="references" />
      <Delimiter />
      <Value>{renderNode(value.references, "")}</Value>
    </Entry>
  </Post>
)

const RenderNavigation = ({ value }: { value: NavigationNode }) => (
  <Navigation>
    <Entry trailing={true}>
      <Key label="pages" />
      <Delimiter />
      <Value>{renderNode(value.pages, "")}</Value>
    </Entry>
    <Entry trailing={true}>
      <Key label="posts" />
      <Delimiter />
      <Value>{renderNode(value.posts, "")}</Value>
    </Entry>
  </Navigation>
)
